import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { RBACProvider } from './contexts/RBACContext';
import { ProtectedRoute, PublicRoute } from './utils/routeGuard';
import Layout from './components/layout/Layout';
import { Toaster } from 'react-hot-toast';
import { TenantLevel } from './types';
import ToastProvider from './components/ui/ToastProvider';

// Pages
import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import DashboardPage from './pages/DashboardPage';
import TenantListPage from './pages/tenants/TenantListPage';
import TenantFormPage from './pages/tenants/TenantFormPage';
import UserFormPage from './pages/users/UserFormPage';
import ProductsListPage from './pages/products/ProductsListPage';
import ProductOnboardingPage from './pages/products/ProductOnboardingPage';
import ProductDetailPage from './pages/products/ProductDetailPage';
import EditProductPage from './pages/products/EditProductPage';
import IntegrateProductPage from './pages/products/IntegrateProductPage';
import IntegrationsPage from './pages/integrations/IntegrationsPage';
import ForwardingPage from './pages/forwarding/ForwardingPage';
import AuditLogPage from './pages/audit/AuditLogPage';
import RBACPage from './pages/rbac/RBACPage';
import ApplicationSettingsPage from './pages/rbac/ApplicationSettingsPage';
import NotFoundPage from './pages/NotFoundPage';
import UnauthorizedPage from './pages/UnauthorizedPage';

// Change Management Pages
import ChangesDashboard from './pages/changes/ChangesDashboard';
import ChangesListPage from './pages/changes/ChangesListPage';
import ChangeFormPage from './pages/changes/ChangeFormPage';
import ChangeTemplatesPage from './pages/changes/ChangeTemplatesPage';
import TemplateFormPage from './pages/changes/TemplateFormPage';
import ChangeDetailPage from './pages/changes/ChangeDetailPage';
import TeamManagementPage from './pages/changes/TeamManagementPage';
// Remove the imports that don't exist
// import TenantDetailPage from './pages/tenants/TenantDetailPage';
// import ProductsPage from './pages/products/ProductsPage';
// import IntegrationDetailPage from './pages/integrations/IntegrationDetailPage';
// import LoggerProductsPage from './pages/logger/LoggerProductsPage';
// import LoggerIntegrationsPage from './pages/logger/LoggerIntegrationsPage';
// import AddTenantPage from './pages/tenants/AddTenantPage';

const App: React.FC = () => {
  return (
    <Router>
      <ThemeProvider>
        <AuthProvider>
          <RBACProvider>
            <ToastProvider>
              {/* Toast notifications */}
            <Toaster
              position="top-center"
              toastOptions={{
                // Dark theme styling
                style: {
                  background: '#2D3748',
                  color: '#fff',
                  border: '1px solid #4A5568',
                  maxWidth: '400px',
                  width: 'auto',
                  padding: '12px 16px',
                  marginTop: '10px',
                },
                success: {
                  duration: 3000,
                  iconTheme: {
                    primary: '#38A169',
                    secondary: '#fff',
                  },
                },
                error: {
                  duration: 4000,
                  iconTheme: {
                    primary: '#E53E3E',
                    secondary: '#fff',
                  },
                },
              }}
            />
            <Routes>
              {/* Public Routes */}
              <Route element={<PublicRoute />}>
                <Route path="/login" element={<LoginPage />} />
                <Route path="/admin/login" element={<LoginPage />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
              </Route>
              
              {/* Protected Routes */}
              <Route element={<ProtectedRoute />}>
                <Route element={<Layout />}>
                  <Route path="/" element={<Navigate to="/dashboard" replace />} />
                  <Route path="/admin" element={<Navigate to="/dashboard" replace />} />
                  <Route path="/dashboard" element={<DashboardPage />} />
                  <Route path="/admin/dashboard" element={<DashboardPage />} />
                  
                  {/* Primary Add Tenant Route - Directly accessible and prominently displayed */}
                  {/* <Route path="/add-tenant" element={<AddTenantPage />} /> */}
                  
                  {/* Tenant Routes */}
                  <Route path="/tenants" element={<TenantListPage />} />
                  <Route path="/tenants/new" element={<TenantFormPage />} />
                  {/* Use TenantFormPage for detail page as well */}
                  <Route path="/tenants/:id" element={<TenantFormPage />} />
                  
                  {/* Deleted Tenants Route - Redirect to tenants with status=deleted filter */}
                  <Route path="/deleted-tenants" element={<Navigate to="/tenants?status=deleted" replace />} />
                  
                  {/* Partner Routes (alias for L2 tenants) */}
                  <Route path="/partners" element={<Navigate to="/tenants?level=L2" replace />} />
                  <Route path="/partners/new" element={<TenantFormPage initialLevel={TenantLevel.L2} />} />
                  
                  {/* Organization Routes (alias for L3 tenants) */}
                  <Route path="/customers" element={<Navigate to="/tenants?level=L3" replace />} />
                  <Route path="/customers/new" element={<TenantFormPage initialLevel={TenantLevel.L3} />} />
                  
                  {/* User Routes - only forms, main user listing is under tenants users view */}
                  <Route path="/users/new" element={<UserFormPage />} />
                  <Route path="/users/:id" element={<UserFormPage />} />
                  
                  {/* Product Routes */}
                  <Route path="/products" element={<ProductsListPage />} />
                  <Route path="/products/onboarding" element={<ProductOnboardingPage />} />
                  <Route path="/products/:id" element={<ProductDetailPage />} />
                  <Route path="/products/:id/edit" element={<EditProductPage />} />
                  <Route path="/integrate-product/:productId" element={<IntegrateProductPage />} />
                  
                  {/* Integration Routes */}
                  <Route path="/integrations" element={<IntegrationsPage />} />
                  {/* Use ProductDetailPage for integration detail page temporarily */}
                  <Route path="/integrations/:customerId/:productId" element={<ProductDetailPage />} />

                  {/* Forwarding Routes */}
                  <Route path="/forwarding" element={<ForwardingPage />} />
                  
                  {/* Audit Log Routes */}
                  <Route path="/audit-logs" element={<AuditLogPage />} />
                  
                  {/* Access Control Routes */}
                  <Route path="/access-control" element={<RBACPage />} />
                  <Route path="/access-control/applications/:id/settings" element={<ApplicationSettingsPage />} />
                  
                  {/* Logger Routes - Comment out or replace with placeholder components */}
                  <Route path="/logger/products" element={<ProductsListPage />} />
                  <Route path="/logger/integrations" element={<IntegrationsPage />} />
                  
                  {/* Change Management Routes */}
                  {/* View access to change dashboard, list, create, and edit */}
                  <Route element={<ProtectedRoute requiredFeature="changes" />}>
                    <Route path="/change-management" element={<ChangesDashboard />} />
                    <Route path="/changes" element={<ChangesListPage />} />
                    <Route path="/changes/:id" element={<ChangeDetailPage />} />
                    <Route path="/changes/new" element={<ChangeFormPage />} />
                    <Route path="/changes/:id/edit" element={<ChangeFormPage />} />
                  </Route>
                  {/* Manage/configure change management (teams and templates only) */}
                  <Route element={<ProtectedRoute requiredFeature="changes_manage" />}>
                    <Route path="/change-management/teams" element={<TeamManagementPage />} />
                    <Route path="/change-templates" element={<ChangeTemplatesPage />} />
                    <Route path="/change-templates/new" element={<TemplateFormPage />} />
                    <Route path="/change-templates/:id/edit" element={<TemplateFormPage />} />
                  </Route>
                </Route>
              </Route>
              
              {/* Error Routes */}
              <Route path="/unauthorized" element={<UnauthorizedPage />} />
              <Route path="/admin/unauthorized" element={<Navigate to="/unauthorized" replace />} />
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </ToastProvider>
          </RBACProvider>
        </AuthProvider>
      </ThemeProvider>
    </Router>
  );
};

export default App;
